import { useFetchData } from "../../../hooks/useFetch";
import { BaseResponse } from "../../../models/response/base-response";
import { ApiConfig } from "../../../services/api-config";
import {API_DEPARTMENT_ROUTER} from "../../../services/api-config";
import { ATTENDANCE_MODE } from "../../../constants/status";

export enum DepartmentStatusCode {
  INACTIVE = 0,
  ACTIVE = 1,
}

/** Nhân sự thuộc bộ phận (bản rút gọn BE trả kèm trong danh sách / chi tiết bộ phận). */
export interface DepartmentEmployee {
  id: number;
  name: string;
  username: string;
  phone: string;
  email: string;
  avatar: string;
  gender: number;              // 1 = nam, 0 = nữ
  joining_date: string;        // DD/MM/YYYY
  is_driver: number;
  is_owner: number;
  is_all_branch: number;
  privilege_group_id: number;
  status: number;
}

export interface Department {
  id: number;
  company_id: number;
  parent_department_id: number;
  parent_department_name?: string;
  name: string;
  user_count: number;
  status: DepartmentStatusCode;
  description: string;
  created_at: string;
  updated_at: string;
  /** Nhân sự ĐANG HOẠT ĐỘNG thuộc bộ phận — BE trả kèm ở endpoint chi tiết. */
  users?: DepartmentEmployee[];
  /** Chế độ chấm công mặc định của bộ phận — NV kế thừa trừ khi bị override riêng (users.attendance_mode). */
  default_attendance_mode?: ATTENDANCE_MODE;
}

export interface DepartmentListFilters {
  status?: number;           // 1 active, 0 inactive, -1 all
  company_id?: number;
  parent_department_id?: number;
  keyword?: string;
}

export interface DepartmentListResponse {
  list: Department[];
  total_record?: number;
  limit?: number;
}

export interface CountTabsResponse {
  active: number;
  inactive: number;
}

export interface CreateDepartmentPayload {
  company_id: number;
  parent_department_id?: number; // 0 hoặc undefined = không có
  name: string;
  description?: string;
  status?: DepartmentStatusCode;
  default_attendance_mode?: ATTENDANCE_MODE;
}

export interface UpdateDepartmentPayload {
  parent_department_id?: number;
  name: string;
  description?: string;
  status?: DepartmentStatusCode;
  default_attendance_mode?: ATTENDANCE_MODE;
}

export interface ChangeStatusPayload {
  status: DepartmentStatusCode;
}

export const DepartmentService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DepartmentListResponse>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: DepartmentListFilters = { status: -1 }) =>
        request({
          endPoint: API_DEPARTMENT_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Department>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (payload: CreateDepartmentPayload) =>
        request({
          endPoint: API_DEPARTMENT_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Department>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateDepartmentPayload) =>
        request({
          endPoint: API_DEPARTMENT_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  changeStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Department>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number, payload: ChangeStatusPayload) =>
        request({
          endPoint: API_DEPARTMENT_ROUTER.API_CHANGE_STATUS(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  detail: () => { 
    const { request, baseResponse, loading } = useFetchData.Get<Department>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_DEPARTMENT_ROUTER.API_DETAIL(id),
        }),
      response: baseResponse,
      loading: loading,
    }
  },
};

export default DepartmentService;
